import { css } from '@emotion/core'
import { ContentEdit } from '@fs/zion-icon'
import { H2, H3, IconButton, Row, Separator } from '@fs/zion-ui'
import { getNestedOrFloatingElementById, getPersonName, useSlsStateContext } from '@fs/zion-sls'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useOverlayContext } from '../../../../shared/providers/overlayContextProvider/OverlayContext'

const indentCss = css`
  padding-inline-start: 10px;
`

const getFieldValue = (field) =>
  field?.standard?.standardizedText ??
  field?.standardizedText ??
  field?.originalText ??
  (typeof field === 'string' ? field : '')

const organizeEventFields = (event, eventFields, mainType = null) => {
  let mainField
  const secondaryFields = eventFields.reduce((fields, eventField, index) => {
    const fieldValue = getFieldValue(event[eventField.id])
    if (mainType && eventField.fieldType === mainType && !mainField) {
      mainField = fieldValue
    } else {
      fields.push({
        id: eventField?.id || index,
        label: eventField?.label,
        value: fieldValue,
      })
    }
    return fields
  }, [])

  return { mainField, secondaryFields }
}

const EventFields = ({ event, eventFields, title, mainType, bottomSeparator = false }) => {
  const { mainField, secondaryFields } = useMemo(
    () => organizeEventFields(event, eventFields, mainType),
    [event, eventFields, mainType]
  )

  if (!eventFields.length) return null

  return (
    <div>
      <div css={indentCss}>
        <H2 size="H6" supplementary>
          {title}
          {mainField && `: ${mainField}`}
        </H2>
        <Separator size="nano" />
        <div css={indentCss}>
          {secondaryFields.map((field) => (
            <React.Fragment key={field.id}>
              <H3 size="H6" supplementary>
                {field.label}: {field.value}
              </H3>
              <Separator size="nano" />
            </React.Fragment>
          ))}
        </div>
      </div>
      {bottomSeparator && <Separator size="nano" />}
    </div>
  )
}

/**
 * Component for printing out read only event information, such as place, date, and other fields, with an optional edit button.
 * Can show attached person names as well as an optional feature.
 * @param {Object} event - parsed event derived from sls through event utils
 * @param {Function} setCurrentEvent - Function for passing back an event the user chose to edit
 * @param {Array} attachedPersonIds - optional. These people will be shown under an attached to heading
 * @param {Boolean} allowEdits - If true, an edit button will be shown
 * @returns {Element} - Event component
 */
const Event = ({ event, setCurrentEvent, attachedPersonIds, allowEdits }) => {
  const zionSlsState = useSlsStateContext()
  const { openOverlay } = useOverlayContext()
  const { t } = useTranslation()
  const { eventTypeField, placeFields = [], dateFields = [], otherFields = [] } = event || {}

  const handleEdit = useCallback(() => {
    setCurrentEvent(event)
    openOverlay('eventPanel')
  }, [setCurrentEvent, event, openOverlay])

  return (
    <div css={indentCss}>
      <Row>
        <H2 size="H6" alignY="middle" growX>
          {eventTypeField?.label}
        </H2>
        {allowEdits && (
          <IconButton
            Icon={ContentEdit}
            onClick={handleEdit}
            title={t('edit-events.edit-event', 'Edit Event')}
            data-testid="edit-event-button"
            linkName="VerifyAndEditOverlay_EditEvent"
            noShrinkX
          />
        )}
      </Row>
      {!allowEdits && <Separator size="nano" />}
      <EventFields
        event={event}
        eventFields={dateFields}
        title={t('edit-events.fields.date-heading', 'Date')}
        bottomSeparator={placeFields.length > 0}
        mainType="DATE"
      />
      <EventFields
        event={event}
        eventFields={placeFields}
        title={t('edit-events.fields.place-heading', 'Place')}
        bottomSeparator={otherFields.length > 0}
        mainType="PLACE"
      />
      <EventFields event={event} eventFields={otherFields} title={t('edit-events.fields.others-heading', 'Others')} />
      <Separator size="xs" />
      {attachedPersonIds && (
        <div css={indentCss}>
          <H2 size="H6" supplementary>
            {t('edit-events.persons.attachments', 'Attached to')}
          </H2>
          <Separator size="nano" />
          {attachedPersonIds.map((personId) => {
            const attachedPerson = getNestedOrFloatingElementById(zionSlsState.images, personId)
            return (
              <div key={attachedPerson.id}>
                <div css={indentCss}>
                  <H3 size="H6" supplementary>
                    {getPersonName({ person: attachedPerson })}
                  </H3>
                </div>
                <Separator size="nano" />
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default Event
